import React, { useState } from "react";
import { CloseCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';

// ======= 模拟数据结构类型 =======
interface ItemData {
  title: string;
  desc: string;
}

interface GroupData {
  category: string;
  items: ItemData[];
}

// ======= 中文内容 =======
const mockDataZh: GroupData[] = [
  {
    category: "业务领域",
    items: [
      { title: "公司与并购重组", desc: "提供公司设立、股权架构设计、并购重组、公司治理等全方位的法律服务，助力企业优化资源配置和实现战略发展目标。" },
      { title: "合规与风险控制", desc: "建立完善的合规管理体系，识别和评估企业运营中的法律风险，制定风险防控方案，确保企业合规经营。" },
      { title: "劳动与人力资源", desc: "处理劳动合同、薪酬福利、工伤认定、劳动争议等劳动法律事务，为企业人力资源管理工作提供法律支持。" },
      { title: "跨境投资与贸易", desc: "为跨境投资、国际贸易、海外并购等项目提供法律咨询、尽职调查、合同审查等专业服务。" },
      { title: "破产重组与清算", desc: "参与企业破产重整、债务重组、清算注销等程序，维护各方当事人的合法权益。" },
      { title: "民商事争议解决", desc: "代理各类民商事诉讼、仲裁案件，通过专业法律分析和策略制定，为客户争取最佳争议解决结果。" },
      { title: "境内外刑事案件", desc: "为涉嫌刑事犯罪的单位和个人提供辩护服务，包括侦查阶段、审查起诉阶段和审判阶段的全面法律保障。" },
      { title: "知识产权与科技创新", desc: "保护专利、商标、著作权等知识产权，为科技创新企业提供知识产权布局、维权和商业化运营法律服务。" },
      { title: "证券与资本市场", desc: "为企业上市、再融资、债券发行、并购重组等资本市场活动提供专业法律服务。" },
      { title: "数字经济", desc: "为互联网、大数据、人工智能等数字经济企业提供合规经营、数据保护、网络安全等专业法律服务。" },
      { title: "家族财富安全与传承", desc: "设计家族财富管理方案，包括遗嘱规划、信托设立、税务优化等，实现财富的安全传承。" },
      { title: "税务", desc: "提供税务筹划、税务争议解决、跨境税务安排等专业税务法律服务，优化企业税务成本。" },
      { title: "RWA资产通证化合规", desc: "为实物资产通证化项目提供法律架构设计、合规审查、监管沟通等全方位法律服务。" },
      { title: "国际制裁", desc: "协助企业应对国际制裁措施，进行合规审查、风险评估和应对方案制定。" }
    ]
  },
  {
    category: "行业领域",
    items: [
      { title: "金融", desc: "为银行、保险、证券、基金等金融机构提供监管合规、金融产品创新、风险控制等专业法律服务。" },
      { title: "资本市场", desc: "深度参与资本市场各类交易，为上市公司、投资机构提供专业的资本市场法律服务。" },
      { title: "房地产", desc: "涵盖房地产开发、建设、销售、租赁、物业管理等全产业链的法律服务。" },
      { title: "农业和食品", desc: "为农业企业和食品企业提供生产、加工、销售、质量安全等环节的法律保障。" },
      { title: "人工智能", desc: "为AI企业提供算法合规、数据治理、知识产权保护等前沿法律服务。" },
      { title: "汽车、制造业及工业", desc: "服务于汽车制造、装备制造、工业产品等领域的法律需求，涵盖供应链管理、产品质量等。" },
      { title: "消费品与零售", desc: "为消费品企业和零售企业提供产品责任、消费者权益保护、渠道管理等法律服务。" },
      { title: "工程、能源和基础设施", desc: "参与大型工程项目，为能源、交通、市政等基础设施项目提供专业法律支持。" },
      { title: "电信、传媒、娱乐与高科技", desc: "为TMT行业企业提供内容合规、技术许可、网络安全等专业法律服务。" },
      { title: "医疗健康与医药", desc: "服务于医疗机构、医药企业，涵盖医疗纠纷、药品监管、临床试验等专业领域。" }
    ]
  }
];

// ======= 英文内容（示例，可自行改）=======
const mockDataEn: GroupData[] = [
  {
    category: "Practice Areas",
    items: [
      { title: "Corporate & M&A", desc: "Providing comprehensive legal services including company formation, equity structure planning, mergers & acquisitions, and corporate governance." },
      { title: "Compliance & Risk Control", desc: "Building compliance systems, identifying legal risks, and designing risk management solutions." },
      { title: "Labor & HR", desc: "Handling employment contracts, compensation, workplace injury cases, and labor disputes." },
      { title: "Cross-Border Investment & Trade", desc: "Offering legal consulting, due diligence, and contract review for international investment and trade." },
      { title: "Bankruptcy & Restructuring", desc: "Participating in bankruptcy restructuring, debt reorganization, and liquidation procedures." },
      { title: "Civil & Commercial Dispute Resolution", desc: "Representing clients in civil and commercial litigation and arbitration." },
      { title: "Domestic & International Criminal Cases", desc: "Providing defense services in all stages of criminal proceedings." },
      { title: "IP & Technology Innovation", desc: "Protecting patents, trademarks, and copyrights; advising technology companies on IP strategy." },
      { title: "Securities & Capital Markets", desc: "Providing legal services for IPOs, refinancing, bond issuance, and M&A transactions." },
      { title: "Digital Economy", desc: "Advising on data compliance, cybersecurity, and digital business operations." }
    ]
  },
  {
    category: "Industries",
    items: [
      { title: "Finance", desc: "Providing financial compliance and risk control services." },
      { title: "Capital Markets", desc: "Advising on capital market transactions and regulatory matters." },
      { title: "Real Estate", desc: "Covering the full chain of real estate development and operations." },
      { title: "Agriculture & Food", desc: "Supporting agricultural and food enterprises with legal compliance." },
      { title: "Artificial Intelligence", desc: "Advising AI companies on algorithms, data governance, and IP." },
      { title: "Automotive & Manufacturing", desc: "Providing services across supply chain and product compliance." },
      { title: "Consumer Goods & Retail", desc: "Advising on consumer protection, product liability, and retail channels." },
      { title: "Energy, Engineering & Infrastructure", desc: "Supporting major infrastructure projects with legal services." },
      { title: "TMT", desc: "Providing compliance and cybersecurity services for telecom and media." },
      { title: "Healthcare & Pharmaceuticals", desc: "Serving medical institutions and pharmaceutical companies." }
    ]
  }
];

export default function AboutPart6() {
  const [activeItem, setActiveItem] = useState<ItemData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const languageCode = localStorage.getItem('siteLanguage') || 'zh';

  const dataSource = languageCode === 'en' ? mockDataEn : mockDataZh;

  const openModal = (item: ItemData) => {
    setActiveItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveItem(null);
  };

  return (
    <div className="about6-part6-index">
      {dataSource.map((group, groupIndex) => (
        <div key={groupIndex} className="part6-List">
          <h2 className="par6-header">{group.category}</h2>

          <div className="part6-item-List">
            {group.items.map((item, itemIndex) => (
              <div
                key={itemIndex}
                onClick={() => openModal(item)}
                className="border-inner"
              >
                {item.title}
              </div>
            ))}
          </div>
        </div>
      ))}

      <Modal
        open={modalOpen}
        centered
        width={1200}
        closable={false}
        footer={null}
      >
        <div className="plan-content">
          <div className="plan-content-header">
            <div>{activeItem?.title}</div>
            <CloseCircleOutlined
              onClick={closeModal}
              style={{ fontSize: "35px", cursor: "pointer" }}
            />
          </div>
          <div className="plan-line"></div>
          <div className="plan-note-body">
            {activeItem?.desc}
          </div>
        </div>
      </Modal>
    </div>
  );
}
